import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { signedOutPath } from '@/lib/signOutPath';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import ReportIssueDialog from '@/components/support/ReportIssueDialog';
import PrimaryIconRail from './sidebar/PrimaryIconRail';
import SecondaryPanel from './sidebar/SecondaryPanel';
import { useNavSections } from './sidebar/useNavSections';
import {
  getSectionFromPathname,
  isNavItemActive,
  PrimarySectionId,
  SecondaryNavItem,
} from './sidebar/navigation';

export type { PrimarySectionId };

interface AppSidebarProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isSecondaryOpen: boolean;
  setIsSecondaryOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
  /** Tablet width: the panel covers the page, so picking a link closes it. */
  panelOverlays?: boolean;
}

/**
 * The two-level sidebar.
 *
 *   PrimaryIconRail   always visible, one icon per section
 *   SecondaryPanel    the wide panel, collapsible, shows that section's links
 *
 * What goes in the menu lives in `sidebar/navigation.ts`. This file only tracks
 * which section is selected and passes things down.
 */
export default function AppSidebar({
  isOpen,
  setIsOpen,
  isSecondaryOpen,
  setIsSecondaryOpen,
  panelOverlays = false,
}: AppSidebarProps) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, activeExam, setActiveExam, setIsTutorOpen, logout } = useApp();

  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<PrimarySectionId>(() =>
    getSectionFromPathname(pathname, searchParams),
  );

  // Follow the URL, so arriving at a page by any route selects its section.
  useEffect(() => {
    setActiveSection(getSectionFromPathname(pathname, searchParams));
  }, [pathname, searchParams]);

  const sections = useNavSections();
  const currentSection = sections.find(section => section.id === activeSection) ?? sections[0];

  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, setIsOpen]);

  const closeMobileSidebar = () => setIsOpen(false);

  const handleSectionClick = (sectionId: PrimarySectionId) => {
    if (activeSection === sectionId) {
      // Clicking the selected icon again collapses the panel.
      setIsSecondaryOpen(prev => !prev);
    } else {
      setActiveSection(sectionId);
      setIsSecondaryOpen(true);
    }
  };

  const handleSignOut = async () => {
    // Read before logout clears the user: a consultancy's student goes back to its page.
    const home = signedOutPath(user);
    try {
      setIsSigningOut(true);
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsOpen(false);
      setIsSecondaryOpen(false);
      navigate(home);
      setIsSigningOut(false);
    }
  };

  const isItemActive = (item: SecondaryNavItem) => isNavItemActive(item, pathname, searchParams);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden transition-opacity"
          onClick={() => {
            setIsOpen(false);
            setIsSecondaryOpen(false);
          }}
        />
      )}

      <div
        className={`fixed top-0 h-dvh left-0 z-40 hidden lg:flex text-white bg-gradient-to-b from-[#2a56cf] via-primary to-[#132e7d] dark:from-blue-950 dark:via-blue-950 dark:to-slate-950 transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <PrimaryIconRail
          sections={sections}
          activeSection={activeSection}
          isSecondaryOpen={isSecondaryOpen}
          user={user}
          isSigningOut={isSigningOut}
          onSectionClick={handleSectionClick}
          onLogoClick={closeMobileSidebar}
          onReportIssue={() => {
            setIsReportOpen(true);
            setIsOpen(false);
          }}
          onCloseMobile={closeMobileSidebar}
          onSignOut={handleSignOut}
        />

        <SecondaryPanel
          section={currentSection}
          isOpen={isSecondaryOpen}
          activeExam={activeExam}
          user={user}
          isItemActive={isItemActive}
          onExamChange={setActiveExam}
          onOpenTutor={() => {
            setIsTutorOpen(true);
            setIsOpen(false);
          }}
          onNavigate={() => {
            closeMobileSidebar();
            if (panelOverlays) setIsSecondaryOpen(false);
          }}
        />

        {/* Round toggle halfway down the sidebar's edge: hides or shows the menu. */}
        <button
          type="button"
          data-stay
          onClick={() => setIsSecondaryOpen(prev => !prev)}
          title={isSecondaryOpen ? 'Hide menu' : 'Show menu'}
          aria-label={isSecondaryOpen ? 'Hide menu' : 'Show menu'}
          aria-expanded={isSecondaryOpen}
          className="group absolute -right-5 top-1/2 z-50 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-primary text-white shadow-xl shadow-primary/30 transition-all hover:scale-105 hover:bg-primary/90 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:border-slate-900"
        >
          {isSecondaryOpen
            ? <ChevronLeft className="h-6 w-6 transition-transform group-hover:-translate-x-0.5" aria-hidden />
            : <ChevronRight className="h-6 w-6 transition-transform group-hover:translate-x-0.5" aria-hidden />}
        </button>
      </div>

      <ReportIssueDialog open={isReportOpen} onOpenChange={setIsReportOpen} />
    </>
  );
}

